import { ApolloLink, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { sha256 } from 'crypto-hash';
import { createClient } from 'graphql-ws';
import { isEmpty } from 'lodash';
import { User, UserManager } from 'oidc-client-ts';
import { AuthProviderNoUserManagerProps } from 'react-oidc-context';

import { createRetryAuthorized, isInteractiveLoginRequired } from '@api/retryAuthorized';
import { OIDC_EXTRA_TOKEN_PARAMS, oidcConfig } from '@src/setup/config/oidc';
import { normalizeEmailsDeep } from '@src/utils/helperFunctions';

// Single OIDC UserManager shared across every link. The previous implementation
// constructed a new one in every request and on every error — `signinSilent()`
// fired in parallel could trigger overlapping refresh attempts and put the user
// into a redirect loop. One manager guarantees one in-flight refresh at a time.
let cachedUserManager: UserManager | null = null;
const getUserManager = (): UserManager => {
  if (!cachedUserManager) {
    cachedUserManager = new UserManager(oidcConfig as AuthProviderNoUserManagerProps);
  }
  return cachedUserManager;
};

// In-flight refresh deduplication. If two requests both notice an expiring token
// at the same moment, both await the same promise instead of double-spinning the
// silent-signin endpoint.
let refreshInFlight: Promise<User | null> | null = null;
const refreshSession = (): Promise<User | null> => {
  // `refreshInFlight` is `Promise<User | null> | null`. Compare explicitly to
  // `null` so the Promise value isn't implicitly truthy-coerced (which the
  // no-misused-promises rule flags).
  if (refreshInFlight !== null) return refreshInFlight;
  // Forward extraTokenParams (audience) explicitly: signinSilent's refresh-token
  // grant only sends what the CALLER passes — unlike the login code exchange,
  // which pulls extraTokenParams from settings. Without this, renewed tokens are
  // requested without `audience` and can come back missing the staff-context
  // claims (org/location), which the backend then copies into the session.
  const silentArgs = { extraTokenParams: OIDC_EXTRA_TOKEN_PARAMS };
  refreshInFlight = getUserManager().signinSilent(silentArgs)
    .catch((err: unknown) => {
      // Retry once for transient silent-renew failures (iframe timeout, network
      // blip, state/nonce race with a concurrent renew). A genuine re-login
      // error won't recover, so rethrow immediately and let the caller log out.
      if (isInteractiveLoginRequired(err)) throw err;
      return getUserManager().signinSilent(silentArgs);
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

const STORAGE_KEY = `oidc.user:${process.env.REACT_APP_KEYCLOAK_REALM_URL}:${process.env.REACT_APP_KEYCLOAK_CLIENT_ID}`;

function getSession(): User | null {
  try {
    const oidcStorage = sessionStorage.getItem(STORAGE_KEY);
    if (!oidcStorage) return null;
    return User.fromStorageString(oidcStorage);
  } catch {
    return null;
  }
}

// Single redirect guard so a flurry of 401s doesn't enqueue multiple navigations.
let redirecting = false;
const forceLogoutRedirect = async (): Promise<void> => {
  if (redirecting) return;
  redirecting = true;
  try {
    const auth = getUserManager();
    await auth.removeUser();
    await auth.signinRedirect();
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.warn('[forceLogoutRedirect]', err);
  }
};

// eslint-disable-next-line import/prefer-default-export
export const useLinks = () => {
  const linkOptions = {
    uri: process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_PROD_API_URL
      : process.env.REACT_APP_DEV_API_URL,
    headers: {
      'apollo-require-preflight': true,
      'x-forwarded-proto': 'https',
      Accept: 'application/json',
      'Content-Type': 'application/json'
    } as Record<string, any>,
    credentials: 'include',
    fetchOptions: {
      mode: 'cors',
      credentials: 'include'
    }
  };

  // Normalizes email-shaped variables on every outgoing operation.
  const emailNormalizerLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      // eslint-disable-next-line no-param-reassign
      operation.variables = normalizeEmailsDeep(operation.variables);
    }
    return forward(operation);
  });

  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const queryParseLink = new ApolloLink((operation, forward) => {
    const { variables } = operation;

    if (!operation.getContext().body?.variables && variables.searchBy?.querySet) {
      const cleanedQuerySet = variables.searchBy.querySet.filter((q) => Boolean(q)
        && Boolean(q.columnName)
        && !isEmpty(q));
      const qVars = variables;
      if (isEmpty(cleanedQuerySet)) {
        delete qVars.searchBy.querySet;
      } else {
        Object.assign(qVars.searchBy, { querySet: cleanedQuerySet });
      }
      operation.setContext(({ body = {} }) => ({
        body: {
          ...body,
          variables: qVars
        }
      }));
    }
    return forward(operation);
  });

  const uploadLink = createUploadLink(linkOptions);

  // Subscriptions over WebSocket. `connectionParams` is invoked on every
  // (re)connect so the latest token is sent — without this, subscriptions
  // were fully unauthenticated.
  const wsLink = new GraphQLWsLink(
    createClient({
      url: process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_PROD_WS_HOST
        : process.env.REACT_APP_DEV_WS_HOST,
      lazy: true,
      keepAlive: 10_000,
      retryAttempts: 5,
      shouldRetry: () => true,
      connectionParams: () => {
        const user = getSession();
        return user?.access_token
          ? { Authorization: `Bearer ${user.access_token}` }
          : {};
      },
      on: {
        // Server emits close code 4401 when the subscription auth token is
        // expired/invalid. Kick off a silent refresh so `retryAttempts: 5`
        // reconnects with a fresh token pulled by `connectionParams()` on the
        // next handshake. Dedup is handled inside `refreshSession()` so this
        // is safe even if multiple subscriptions close simultaneously.
        closed: (event) => {
          const closeEvent = event as { code?: number } | undefined;
          if (closeEvent?.code === 4401) {
            void refreshSession().catch(() => forceLogoutRedirect());
          }
        }
      }
    })
  );

  const persistedLink = createPersistedQueryLink({ sha256 }).concat(
    new BatchHttpLink({
      batchMax: 5,
      batchInterval: 20,
      ...linkOptions
    })
  );

  // Mutations bypass batching (BatchHttp can reorder relative to user expectation
  // and adds latency) and the upload link handles multipart bodies.
  const splitHttpLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation !== 'mutation';
    },
    persistedLink,
    uploadLink
  );

  const authMiddleware = setContext(async (_, { headers }) => {
    const user = getSession();
    if (!user) return { headers };

    // Refresh proactively when within 60s of expiry so the next request goes out
    // with a fresh token instead of needing the errorLink to retry.
    const tokenExpiry = user.expires_at;
    const now = Math.floor(Date.now() / 1000);

    if (tokenExpiry && (tokenExpiry - now) < 60) {
      try {
        await refreshSession();
      } catch (err) {
        // Only force logout when the session genuinely needs re-auth. On a
        // transient refresh failure, send the request anyway — it 401s and the
        // errorLink retry gets another shot rather than nuking a valid session.
        if (isInteractiveLoginRequired(err)) await forceLogoutRedirect();
        return { headers };
      }
    }

    // Re-read after potential refresh
    const fresh = getSession() ?? user;
    return {
      headers: {
        ...headers,
        ...(fresh?.access_token && {
          Authorization: `Bearer ${fresh.access_token}`
        })
      }
    };
  });

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    splitHttpLink
  );

  // Retry-once-on-401 link. If a request comes back unauthorized we silently
  // refresh and replay; we only redirect to login when the refresh failure
  // genuinely needs re-auth (transient failures keep the session). The dedup
  // around `refreshSession()` ensures parallel 401s share a single refresh.
  const retryAuthorized = createRetryAuthorized({
    refreshSession,
    getSession,
    forceLogoutRedirect
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      if (process.env.NODE_ENV !== 'production') {
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.warn(`[GraphQL error]: ${message} | path=${String(path)} | loc=${String(locations)}`);
        });
      }

      const unauthorized = graphQLErrors.some(
        (err) => err.extensions?.code === 'UNAUTHENTICATED'
          || err.message.includes('Unauthorized')
      );
      if (unauthorized) {
        return retryAuthorized(operation, forward);
      }
    }

    if (networkError && 'statusCode' in networkError && (networkError as any).statusCode === 401) {
      return retryAuthorized(operation, forward);
    }

    if (networkError && process.env.NODE_ENV !== 'production') {
      console.warn(`[Network error]: ${String(networkError)}`);
    }

    return undefined;
  });

  return { persistedLink, wsLink, errorLink, splitLink, authMiddleware, emailNormalizerLink };
};
